/**
 * Presentation helpers for the escalation review queue (ADR-0094
 * Slice 3) — shared by `EscalationList` (row cards) and
 * `EscalationDetail` (the detail panel) so the pill tones, the
 * time-to-timeout copy, and the money formatting agree across both
 * surfaces.
 */

import { formatIntentAmount } from '@sill/audit/labels';

import type { PillTone } from '../../components/atoms/index.js';
import type { EscalationStatus, EscalationSummary } from '../../api/escalations.js';

/**
 * Status → pill tone + uppercase label. Palette per CLAUDE.md §4:
 * sky=pending, mint=approved (confirmed), coral=rejected,
 * amber=timed_out (escalate/warning).
 */
export function statusPill(status: EscalationStatus): { tone: PillTone; label: string } {
  switch (status) {
    case 'pending':
      return { tone: 'sky', label: 'PENDING' };
    case 'approved':
      return { tone: 'mint', label: 'APPROVED' };
    case 'rejected':
      return { tone: 'coral', label: 'REJECTED' };
    case 'timed_out':
      return { tone: 'amber', label: 'TIMED OUT' };
  }
}

/**
 * Left-border accent color key for a row card, keyed off status. Returns
 * the token name the caller resolves against `useC()` (sky/mint/coral/
 * amber → `*Fg`).
 */
export function statusAccentToken(status: EscalationStatus): 'skyFg' | 'mintFg' | 'coralFg' | 'amberFg' {
  switch (status) {
    case 'pending':
      return 'skyFg';
    case 'approved':
      return 'mintFg';
    case 'rejected':
      return 'coralFg';
    case 'timed_out':
      return 'amberFg';
  }
}

export interface TimeoutDisplay {
  /** Human countdown, e.g. "2h 14m left" / "expired". */
  readonly label: string;
  /** True when < 1h remains (or already expired) — drives the red accent. */
  readonly imminent: boolean;
}

/**
 * Compute a static time-to-timeout string from the `timeout_at` ISO
 * deadline. Static (not a live 1s ticker) — it refreshes on the queue's
 * 10s poll, matching the Mandates list cadence and avoiding a
 * per-second re-render of every row card. `imminent` flags the final
 * hour so the caller can run the countdown red/amber.
 */
export function timeToTimeout(timeout_at: string, now: number = Date.now()): TimeoutDisplay {
  const deadline = new Date(timeout_at).getTime();
  if (Number.isNaN(deadline)) {
    return { label: timeout_at, imminent: false };
  }
  const remaining_ms = deadline - now;
  if (remaining_ms <= 0) {
    return { label: 'expired', imminent: true };
  }
  const total_minutes = Math.floor(remaining_ms / 60_000);
  const imminent = remaining_ms < 3_600_000;
  if (total_minutes < 60) {
    return { label: `${total_minutes}m left`, imminent };
  }
  const hours = Math.floor(total_minutes / 60);
  const minutes = total_minutes % 60;
  if (hours < 24) {
    return { label: `${hours}h ${minutes}m left`, imminent };
  }
  const days = Math.floor(hours / 24);
  const rem_hours = hours % 24;
  return { label: `${days}d ${rem_hours}h left`, imminent };
}

/**
 * Format a MAJOR-unit amount (whole dollars — NOT cents) with the row's
 * currency, defaulting to USD. `intent.max_amount` / `intent.quoted_total`
 * are dollars end-to-end (the rail multiplies by 100 at execute time), so
 * this delegates to `formatIntentAmount` — the same helper the Mandate
 * panel uses — keeping the two surfaces consistent (USD → `$X.XX`, else
 * `<amount> <currency>`). Call sites read
 * `formatMoney(summary.max_amount, summary.currency)`.
 */
export function formatMoney(amount: number, currency?: string): string {
  return formatIntentAmount(amount, currency ?? 'USD');
}

/** True when the held mandate is a refund request (ADR-0099). */
export function isRefundSummary(summary: EscalationSummary): boolean {
  return summary.action === 'request_refund';
}

/**
 * Humanize a snake_case refund reason code for display:
 * `wrong_item` → `Wrong item`. Unknown codes degrade to the same
 * transformation (the enum is server-validated; this is display only).
 */
export function humanizeReasonCode(code: string): string {
  const cleaned = code.trim().replace(/[_-]+/g, ' ');
  if (cleaned.length === 0) return code;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
}

/**
 * Refund card/detail headline (ADR-0099 review UX):
 * `Refund request · Wrong item · up to $50.00`. `max_amount` is the
 * agent-signed CAP, hence "up to" — the actual refund is bounded by it,
 * never asserted by it.
 */
export function refundHeadline(summary: EscalationSummary): string {
  const reason =
    summary.reason_code !== undefined
      ? humanizeReasonCode(summary.reason_code)
      : 'Unspecified reason';
  const cap =
    summary.max_amount !== undefined
      ? ` · up to ${formatMoney(summary.max_amount, summary.currency)}`
      : '';
  return `Refund request · ${reason}${cap}`;
}
